import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { HoverIdComponent } from '../hover-id/hover-id.component';

export interface SlotSelectionOverlaySegment {
  id: string;
  startPercent: number;
  widthPercent: number;
  timeLabel: string;
  primaryLabel?: string | null;
  status: string;
  past: boolean;
  selected: boolean;
  highlighted: boolean;
  selectable: boolean;
  title?: string | null;
}

@Component({
  selector: 'lib-slot-selection-overlay',
  standalone: true,
  imports: [HoverIdComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @for (segment of segments; track segment.id) {
      <lib-hover-id
        class="slot-selection-overlay__slot-hover"
        [value]="segment.title"
        [style.left.%]="segment.startPercent"
        [style.width.%]="segment.widthPercent"
      >
        <button
          type="button"
          class="slot-selection-overlay__slot"
          [class.slot-selection-overlay__slot--selected-start]="segment.selected"
          [class.slot-selection-overlay__slot--selected-block]="segment.highlighted"
          [class.slot-selection-overlay__slot--available]="segment.status === 'available'"
          [class.slot-selection-overlay__slot--reserved]="segment.status === 'reserved'"
          [class.slot-selection-overlay__slot--blocked]="segment.status === 'blocked'"
          [class.slot-selection-overlay__slot--completed]="segment.status === 'completed'"
          [class.slot-selection-overlay__slot--past]="segment.past"
          [disabled]="disabled || !segment.selectable"
          (click)="slotSelected.emit(segment.id)"
        >
          <span class="slot-selection-overlay__content">
            <span class="slot-selection-overlay__time">{{ segment.timeLabel }}</span>
            @if (segment.primaryLabel) {
              <span class="slot-selection-overlay__primary">{{ segment.primaryLabel }}</span>
            }
          </span>
        </button>
      </lib-hover-id>
    }
  `,
  styles: [`
    :host {
      position: absolute;
      inset: 0;
      display: block;
      z-index: 1;
    }

    .slot-selection-overlay__slot-hover {
      position: absolute;
      top: 0;
      bottom: 0;
      min-width: 1px;
      display: block;
    }

    .slot-selection-overlay__slot {
      width: 100%;
      height: 100%;
      border: 1px solid transparent;
      border-radius: 9px;
      padding: 6px;
      display: grid;
      gap: 2px;
      text-align: left;
      cursor: pointer;
      background: transparent;
      color: rgba(15, 23, 42, 0.88);
    }

    .slot-selection-overlay__slot:disabled {
      cursor: not-allowed;
      opacity: 1;
    }

    .slot-selection-overlay__slot--reserved,
    .slot-selection-overlay__slot--blocked,
    .slot-selection-overlay__slot--completed {
      color: rgba(51, 65, 85, 0.78);
    }

    .slot-selection-overlay__slot--past {
      color: rgba(15, 23, 42, 0.56);
    }

    .slot-selection-overlay__slot--selected-block {
      border-color: #93c5fd;
      box-shadow: 0 0 0 1px #93c5fd inset;
      background: rgba(239, 246, 255, 0.28);
    }

    .slot-selection-overlay__slot--selected-start {
      border-color: #1d4ed8;
      box-shadow: 0 0 0 1px #1d4ed8 inset;
      background: rgba(219, 234, 254, 0.40);
    }

    .slot-selection-overlay__content {
      display: grid;
      gap: 2px;
      min-width: 0;
      text-shadow: 0 1px 1px rgba(255, 255, 255, 0.36);
    }

    .slot-selection-overlay__time,
    .slot-selection-overlay__primary {
      font-size: 10px;
      line-height: 1.2;
      font-weight: 700;
      overflow-wrap: anywhere;
    }

    .slot-selection-overlay__primary {
      color: inherit;
    }
  `],
})
export class SlotSelectionOverlayComponent {
  @Input() public segments: SlotSelectionOverlaySegment[] = [];
  @Input() public disabled = false;

  @Output() public readonly slotSelected = new EventEmitter<string>();
}
